import { Autotractor } from "protos/atk/autotractor";
import { classes } from "common/theme/theme";
import {
  ConfigValidationDetails,
  ConfigValidationStatusCode,
} from "protos/portal/atk_equipment";
import {
  hitchTypeToLocaleString,
  ptoSpeedToLocaleString,
  ptoTypeToLocationString,
} from "common/utils/enumTranslations";
import { MuiIcon } from "common/components/types";
import { Paper, Typography } from "@mui/material";
import { RigValidationContext } from "../RigValidationContext";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import ErrorIcon from "@mui/icons-material/DangerousOutlined";
import PendingIcon from "@mui/icons-material/PendingOutlined";
import React, { useContext } from "react";
import ReadyIcon from "@mui/icons-material/CheckCircleOutlined";
import WarningIcon from "@mui/icons-material/WarningOutlined";

function renderValidationDetails(
  t: TFunction,
  rig: Autotractor,
  details: ConfigValidationDetails
): string {
  let implementName: string = t("utils.descriptors.unknown");
  let requestedHitchType: string = t("utils.descriptors.unknown");
  let requestedPtoType: string = t("utils.descriptors.unknown");
  let requestedPtoSpeed: string = t("utils.descriptors.unknown");

  if (details.implementId) {
    const implement = rig.implements.find(
      (implement) => implement.id === details.implementId
    );

    if (implement) {
      implementName = implement.name;
    }
  }

  if (details.hitchType) {
    requestedHitchType = t(hitchTypeToLocaleString(details.hitchType));
  }

  if (details.ptoType) {
    requestedPtoType = t(ptoTypeToLocationString(details.ptoType));
  }

  if (details.ptoSpeed) {
    requestedPtoSpeed = t(ptoSpeedToLocaleString(details.ptoSpeed));
  }

  switch (details.statusCode) {
    case ConfigValidationStatusCode.VALIDATION_STATUS_NO_TRACTOR: {
      return t("views.autotractor.rigDetails.status.details.noTractor");
    }
    case ConfigValidationStatusCode.VALIDATION_STATUS_HITCH_TYPE_NOT_UNIQUE: {
      return t(
        "views.autotractor.rigDetails.status.details.invalidTractorConfig",
        { details: details.statusCode.toString() }
      );
    }
    case ConfigValidationStatusCode.VALIDATION_STATUS_UNSPECIFIED_PTO_REQUIREMENT: {
      return t(
        "views.autotractor.rigDetails.status.details.invalidImplementConfig",
        {
          implementName,
          details: details.statusCode.toString(),
        }
      );
    }
    case ConfigValidationStatusCode.VALIDATION_STATUS_NO_COMPATIBLE_HITCH: {
      return t(
        "views.autotractor.rigDetails.status.details.noCompatibleHitch",
        {
          implementName,
          requestedHitchType,
        }
      );
    }
    case ConfigValidationStatusCode.VALIDATION_STATUS_PTO_SPEED_NOT_SUPPORTED: {
      return t("views.autotractor.rigDetails.status.details.badPtoSpeed", {
        implementName,
        requestedPtoType,
        requestedPtoSpeed,
      });
    }
    case ConfigValidationStatusCode.VALIDATION_STATUS_SCV_CONNECTION_INVALID_INPUT_ID:
    case ConfigValidationStatusCode.VALIDATION_STATUS_SCV_CONNECTION_INVALID_OUTPUT_ID: {
      return t("views.autotractor.rigDetails.status.details.invalidRigConfig", {
        details: `${details.statusCode}; ${details.scvConnectionId}`,
      });
    }
    case ConfigValidationStatusCode.VALIDATION_STATUS_TRACTOR_CAPTURE_NOT_COMPLETE: {
      return t(
        "views.autotractor.rigDetails.status.details.tractorCaptureNotComplete"
      );
    }
    case ConfigValidationStatusCode.VALIDATION_STATUS_IMPLEMENT_CAPTURE_NOT_COMPLETE: {
      return t(
        "views.autotractor.rigDetails.status.details.implementCaptureNotComplete",
        { implementName }
      );
    }
    default: {
      return t(
        "views.autotractor.rigDetails.status.details.unknownStatusCode",
        { details: details.statusCode.toString() }
      );
    }
  }
}

export const RigStatus: React.FC = () => {
  const { t } = useTranslation();
  const { rig, validationResult, validationResultLoading } =
    useContext(RigValidationContext);

  let className: string;
  let StatusIcon: MuiIcon;
  let statusText: string;
  let detailsList: ConfigValidationDetails[];

  if (validationResultLoading) {
    className = "";
    StatusIcon = PendingIcon;
    statusText = t("views.autotractor.rigDetails.status.loading");
    detailsList = [];
  } else if (validationResult && validationResult.errors.length > 0) {
    className = "bg-error-800 text-white";
    StatusIcon = ErrorIcon;
    statusText = t("views.autotractor.rigDetails.status.notReadyForAutonomy");
    detailsList = validationResult.errors;
  } else if (validationResult && validationResult.warnings.length > 0) {
    className = "bg-warning text-black";
    StatusIcon = WarningIcon;
    statusText = t(
      "views.autotractor.rigDetails.status.readyForAutonomyWarnings"
    );
    detailsList = validationResult.warnings;
  } else {
    className = "bg-success-800 text-white";
    StatusIcon = ReadyIcon;
    statusText = t("views.autotractor.rigDetails.status.readyForAutonomy");
    detailsList = [];
  }

  return (
    rig && (
      <Paper className={classes(className, "flex flex-col p-5 rounded-xl")}>
        <div className="flex flex-row gap-4 text-xl items-center">
          <StatusIcon />
          <Typography variant="h6">{statusText}</Typography>
        </div>
        {detailsList.length > 0 && (
          <ul>
            {detailsList.map((detail, index) => (
              <li key={index}>{renderValidationDetails(t, rig, detail)}</li>
            ))}
          </ul>
        )}
      </Paper>
    )
  );
};
